// ============================================================
// FILE: JobFeedDetail.js
// LOCATION: C:\gardenmarket\web\src\pages\gardener\JobFeedDetail.js
// ============================================================
// PURPOSE:
//   Right pane of the gardener job feed master-detail layout.
//   Shows a job's full details from the gardener's perspective —
//   description, photos, skills, budget, buyer profile link,
//   and a toolbar with "Submit Quote" or "✓ Quoted" action.
//
//   This is distinct from BookingDetailContent.js (which is for
//   confirmed bookings) — this is the PRE-QUOTE view.
//
// PROPS:
//   jobId       — the job_id to load
//   onQuoted    — called after a quote is successfully submitted
//                 so the parent can refresh the list
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';
import DetailToolbar from '../../components/DetailToolbar';
import ExclusiveStatusCard from '../../components/ExclusiveStatusCard';
import { colors, radius, space, card, cardHeader, statusPill, buyerStatusLabels } from '../../theme/theme';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000';

export default function JobFeedDetail({ jobId, canQuote = true, onQuoted }) {
    const [job,        setJob]        = useState(null);
    const [myQuote,    setMyQuote]    = useState(null);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState('');
    const [withdrawing,setWithdrawing]= useState(false);

    // Quote form state — used for both new quote and editing existing
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [isEditing,     setIsEditing]     = useState(false);
    const [quoteForm, setQuoteForm] = useState({
        quoted_price: '', estimated_hours: '', proposed_date: '', notes: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [quoteError, setQuoteError] = useState('');

    const loadJob = async () => {
        const r = await client.get(`/jobs/${jobId}`);
        setJob(r.data.job);
    };

    useEffect(() => {
        setLoading(true);
        setError('');
        setShowQuoteForm(false);
        setMyQuote(null);
        setIsEditing(false);
        setQuoteForm({ quoted_price:'', estimated_hours:'', proposed_date:'', notes:'' });
        Promise.all([
            loadJob(),
            client.get(`/quotes/my/${jobId}`)
                .then(r => setMyQuote(r.data.quote || null))
                .catch(() => setMyQuote(null)),
        ]).catch(err => setError(err.response?.data?.error || 'Failed to load job.'))
          .finally(() => setLoading(false));
    }, [jobId]);

    const handleSubmitQuote = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setQuoteError('');
        try {
            await client.post('/quotes', {
                job_id:          jobId,
                quoted_price:    parseFloat(quoteForm.quoted_price),
                estimated_hours: quoteForm.estimated_hours ? parseInt(quoteForm.estimated_hours) : null,
                proposed_date:   quoteForm.proposed_date || null,
                notes:           quoteForm.notes || null,
            });
            await loadJob();
            setShowQuoteForm(false);
            setIsEditing(false);
            if (onQuoted) onQuoted();
        } catch (err) {
            setQuoteError(err.response?.data?.error || 'Failed to submit quote.');
        } finally { setSubmitting(false); }
    };

    if (loading) return <div style={styles.loading}>Loading job...</div>;
    if (!job)    return <div style={styles.loading}>{error}</div>;

    const postingPhotos = (job.photos || []).filter(p => !p.photo_type || p.photo_type === 'job_posting');

    // Withdraw and resubmit handler
    const handleWithdraw = async () => {
        if (!myQuote) return;
        setWithdrawing(true);
        try {
            await client.post(`/quotes/${myQuote.quote_id}/withdraw`);
            setMyQuote(null);
            setShowQuoteForm(false);
            setIsEditing(false);
            const r = await loadJob();
        } catch (err) { setError(err.response?.data?.error || 'Failed to withdraw.'); }
        finally { setWithdrawing(false); }
    };

    // Edit quote — pre-fill form with current values then withdraw+resubmit
    const handleEditQuote = () => {
        if (!myQuote) return;
        setQuoteForm({
            quoted_price:    myQuote.quoted_price || '',
            estimated_hours: myQuote.estimated_hours || '',
            proposed_date:   myQuote.proposed_date ? myQuote.proposed_date.split('T')[0] : '',
            notes:           myQuote.notes || '',
        });
        setIsEditing(true);
        setShowQuoteForm(true);
    };

    // Toolbar — respects credit gate and quoted status
    const toolbarActions = !!myQuote
        ? [
            { label: '✏️ Edit My Quote',  onClick: handleEditQuote,  style: 'secondary' },
            { label: withdrawing ? 'Withdrawing...' : '↩ Withdraw', onClick: handleWithdraw, style: 'danger', disabled: withdrawing },
          ]
        : canQuote
        ? [{ label: '💬 Submit a Quote', onClick: () => { setIsEditing(false); setShowQuoteForm(v => !v); }, style: 'primary' }]
        : [];

    return (
        <div>
            <DetailToolbar actions={toolbarActions} />

            {/* Directed-job status. Renders nothing on a normal feed
                job. Lets the gardener pass on a job they cannot take,
                rather than leaving the customer waiting on a referral
                job no one else can see. */}
            <ExclusiveStatusCard jobId={jobId} role="gardener" onChanged={loadJob} />

            {/* Credit blocked notice */}
            {!canQuote && !myQuote && (
                <div style={styles.blockedNotice}>
                    <span>🔒 Top up your credits to submit a quote on this job.</span>
                    <Link to="/gardener/credits" style={styles.blockedLink}>Top up →</Link>
                </div>
            )}

            {/* Quoted summary card — shows current quote with edit option */}
            {!!myQuote && (
                <div style={styles.quotedCard}>
                    <div style={styles.quotedCardHeader}>
                        <span style={styles.quotedCardTitle}>✓ Your submitted quote</span>
                        <span style={styles.quotedPrice}>${Number(myQuote.quoted_price).toFixed(2)}</span>
                    </div>
                    <div style={styles.quotedMeta}>
                        {myQuote.estimated_hours && <span>⏱ ~{myQuote.estimated_hours}hrs</span>}
                        {myQuote.proposed_date && <span>📅 {new Date(myQuote.proposed_date).toLocaleDateString()}</span>}
                        {myQuote.notes && <span style={{fontStyle:'italic'}}>"{myQuote.notes}"</span>}
                    </div>
                    <p style={styles.quotedHint}>
                        Discussed a different price with the buyer? Use <strong>Edit My Quote</strong> to update your quote — the buyer will see the new amount.
                    </p>
                </div>
            )}

            {/* Quote form — shown inline when toolbar button clicked */}
            {showQuoteForm && (
                <form onSubmit={handleSubmitQuote} style={styles.quoteForm}>
                    <h3 style={styles.quoteFormTitle}>{isEditing ? '✏️ Edit Your Quote' : 'Submit Your Quote'}</h3>
                    {isEditing && (
                        <p style={{fontSize:'0.82rem', color:colors.textMuted, marginBottom:'12px'}}>
                            Update your price or details. This will withdraw your current quote and submit the new one.
                        </p>
                    )}
                    {quoteError && <div style={styles.error}>{quoteError}</div>}
                    <div className="gh-grid-3">
                        <div style={styles.field}>
                            <label style={styles.label}>Your price ($) *</label>
                            <input type="number" min="0" step="1" required
                                value={quoteForm.quoted_price}
                                onChange={e => setQuoteForm({...quoteForm, quoted_price: e.target.value})}
                                style={styles.input} placeholder="e.g. 120" />
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Est. hours</label>
                            <input type="number" min="0" step="0.5"
                                value={quoteForm.estimated_hours}
                                onChange={e => setQuoteForm({...quoteForm, estimated_hours: e.target.value})}
                                style={styles.input} placeholder="e.g. 3" />
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Available date</label>
                            <input type="date"
                                value={quoteForm.proposed_date}
                                onChange={e => setQuoteForm({...quoteForm, proposed_date: e.target.value})}
                                style={styles.input} />
                        </div>
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Notes to buyer</label>
                        <textarea
                            value={quoteForm.notes}
                            onChange={e => setQuoteForm({...quoteForm, notes: e.target.value})}
                            style={{...styles.input, height:'80px', resize:'vertical'}}
                            placeholder="Describe your approach, experience with this type of job, etc." />
                    </div>
                    <div style={{display:'flex', gap:'8px'}}>
                        <button type="submit" disabled={submitting}
                            style={{padding:'9px 20px', backgroundColor:colors.brand, color:'white', border:'none', borderRadius:radius.sm, cursor:'pointer', fontWeight:600, fontSize:'0.9rem'}}>
                            {submitting ? (isEditing ? 'Updating...' : 'Submitting...') : (isEditing ? 'Update Quote' : 'Submit Quote')}
                        </button>
                        <button type="button" onClick={() => setShowQuoteForm(false)}
                            style={{padding:'9px 16px', backgroundColor:'white', color:colors.textMuted, border:`1px solid ${colors.border}`, borderRadius:radius.sm, cursor:'pointer'}}>
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {error && <div style={styles.error}>{error}</div>}

            {/* Job header */}
            <div style={styles.header}>
                <h2 style={styles.title}>{job.title} <span style={styles.jobIdTag}>#{job.job_id}</span></h2>
                <div style={styles.metaRow}>
                    <span style={statusPill(job.status_code)}>{buyerStatusLabels[job.status_code] || job.status_code}</span>
                    <span style={styles.metaItem}>📍 {job.suburb_name} {job.postcode}</span>
                    {job.preferred_date && <span style={styles.metaItem}>📅 {new Date(job.preferred_date).toLocaleDateString()}</span>}
                    {job.budget && <span style={styles.metaItem}>💰 Budget: ${job.budget}</span>}
                </div>
                {/* Buyer profile link */}
                <div style={styles.buyerRow}>
                    <span style={styles.postedBy}>Posted by </span>
                    <Link to={`/buyer-profile/${job.buyer_user_id}`} style={styles.buyerLink}>
                        👤 {job.buyer_name}
                    </Link>
                    <span style={styles.postedDate}>
                        {job.posted_at ? ` · ${new Date(job.posted_at).toLocaleDateString()}` : ''}
                    </span>
                </div>
            </div>

            <div className="gh-grid-2">
                <div>
                    <div style={card}>
                        <h3 style={cardHeader}>Job Description</h3>
                        <p style={styles.desc}>{job.description}</p>
                    </div>

                    {job.skills?.length > 0 && (
                        <div style={card}>
                            <h3 style={cardHeader}>Skills Required</h3>
                            <div style={styles.chips}>
                                {job.skills.map(s => (
                                    <span key={s.skill_id} style={styles.chip}>{s.skill_name}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {postingPhotos.length > 0 && (
                        <div style={card}>
                            <h3 style={cardHeader}>Job Photos</h3>
                            <div className="gh-grid-photos">
                                {postingPhotos.map(p => (
                                    <img key={p.photo_id}
                                        src={`${API_BASE}${p.storage_url}`}
                                        alt={p.caption || 'Job photo'}
                                        style={styles.photo}
                                        onError={e => e.target.style.display='none'} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    {/* Quick summary card */}
                    <div style={card}>
                        <h3 style={cardHeader}>Job Summary</h3>
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Type</span>
                            <span>{job.job_type_label || job.job_type_code || '—'}</span>
                        </div>
                        {myQuote ? (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>
                                    {job.status_code === 'accepted' ? 'Accepted price' : 'Your quote'}
                                </span>
                                <span style={{color:colors.brand, fontWeight:800, fontSize:'1.1rem'}}>
                                    ${Number(myQuote.quoted_price).toFixed(2)}
                                </span>
                            </div>
                        ) : job.budget ? (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Buyer's budget</span>
                                <span style={{color:colors.brand, fontWeight:700}}>${job.budget}</span>
                            </div>
                        ) : null}
                        {job.preferred_date && (
                            <div style={styles.summaryRow}>
                                <span style={styles.summaryLabel}>Preferred date</span>
                                <span>{new Date(job.preferred_date).toLocaleDateString()}</span>
                            </div>
                        )}
                        <div style={styles.summaryRow}>
                            <span style={styles.summaryLabel}>Location</span>
                            <span>{job.suburb_name} {job.postcode}, {job.state}</span>
                        </div>
                    </div>

                    {/* Buyer info card */}
                    <div style={card}>
                        <h3 style={cardHeader}>About the Buyer</h3>
                        <p style={{fontWeight:700, color:colors.text, marginBottom:'8px'}}>{job.buyer_name}</p>
                        <Link to={`/buyer-profile/${job.buyer_user_id}`} style={styles.viewProfileBtn}>
                            View Profile →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    loading:       { textAlign:'center', padding:'60px', color:colors.textMuted },
    blockedNotice: { display:'flex', justifyContent:'space-between', alignItems:'center', backgroundColor:'#FEF2F2', border:'1px solid #FECACA', borderRadius:radius.sm, padding:'12px 16px', marginBottom:'16px', fontSize:'0.9rem', color:'#991B1B' },
    blockedLink:   { color:'#991B1B', fontWeight:700, textDecoration:'none', marginLeft:'12px', whiteSpace:'nowrap' },
    quotedCard:       { backgroundColor:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:radius.md, padding:'14px 16px', marginBottom:'16px' },
    quotedCardHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' },
    quotedCardTitle:  { fontSize:'0.88rem', fontWeight:700, color:colors.brand },
    quotedPrice:      { fontSize:'1.4rem', fontWeight:800, color:colors.brand },
    quotedMeta:       { display:'flex', gap:'12px', fontSize:'0.82rem', color:colors.textMuted, marginBottom:'8px', flexWrap:'wrap' },
    quotedHint:       { fontSize:'0.78rem', color:colors.textFaint, marginBottom:0, lineHeight:1.4 },
    quotedBanner:     { backgroundColor:'#e8f5e9', border:'1px solid #a5d6a7', borderRadius:radius.sm, padding:'12px 16px', marginBottom:'16px', fontSize:'0.9rem', color:colors.brand },
    quoteForm:     { backgroundColor:'white', border:`1px solid ${colors.borderSoft}`, borderRadius:radius.md, padding:space.lg, marginBottom:'16px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' },
    quoteFormTitle:{ fontSize:'1rem', fontWeight:700, color:colors.brand, marginBottom:'14px' },
    // quoteGrid / grid / photoGrid now live in responsive.css as
    // .gh-grid-3 / .gh-grid-2 / .gh-grid-photos — inline styles
    // cannot be overridden by media queries, so the column
    // definitions must stay out of this file.
    field:         { marginBottom:'14px' },
    label:         { display:'block', fontWeight:600, marginBottom:'5px', color:'#333', fontSize:'0.85rem' },
    // fontSize MUST stay at 16px: iOS Safari force-zooms the page
    // when a focused input is smaller, and it does not zoom back out.
    input:         { width:'100%', padding:'10px 12px', border:`1px solid ${colors.border}`, borderRadius:radius.sm, fontSize:'16px', boxSizing:'border-box' },
    error:         { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:radius.sm, padding:'10px', color:'#c0392b', marginBottom:'12px', fontSize:'0.85rem' },
    header:        { marginBottom:space.lg, padding:'0 4px' },
    title:         { fontSize:'1.35rem', fontWeight:700, color:colors.text, marginBottom:'10px' },
    jobIdTag:      { fontSize:'0.85rem', color:colors.textFaint, fontWeight:400 },
    metaRow:       { display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'center', marginBottom:'8px' },
    metaItem:      { color:colors.textMuted, fontSize:'0.85rem' },
    buyerRow:      { display:'flex', alignItems:'center', gap:'4px', marginTop:'6px' },
    postedBy:      { fontSize:'0.82rem', color:colors.textFaint },
    buyerLink:     { fontSize:'0.85rem', color:colors.brand, fontWeight:600, textDecoration:'none' },
    postedDate:    { fontSize:'0.82rem', color:colors.textFaint },
    desc:          { color:colors.textMuted, lineHeight:'1.6', fontSize:'0.92rem' },
    chips:         { display:'flex', flexWrap:'wrap', gap:'8px' },
    chip:          { padding:'5px 13px', borderRadius:'999px', backgroundColor:colors.brandLight, color:colors.brand, fontSize:'0.8rem', fontWeight:500 },
    photo:         { width:'100%', height:'90px', objectFit:'cover', borderRadius:radius.sm },
    summaryRow:    { display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${colors.border}`, fontSize:'0.88rem', color:colors.text },
    summaryLabel:  { color:colors.textMuted },
    viewProfileBtn:{ display:'inline-block', marginTop:'4px', fontSize:'0.85rem', color:colors.brand, fontWeight:600, textDecoration:'none' },
};
